/**
 * Learn audio singleton — the web port of iOS AlphabetCourseVoice.
 *
 *  - Narration is ElevenLabs (via the backend quick-tts endpoint), never the
 *    browser's robotic SpeechSynthesis. Lines are cached in CacheStorage so
 *    they survive reloads, and prefetched on course entry so the first prompt
 *    plays instantly.
 *  - Strict FIFO queue with interrupt semantics: nothing overlaps.
 *  - Autoplay unlock: the first user gesture resumes an AudioContext and plays
 *    a silent buffer; until then, speak() requests are queued, not dropped.
 *  - SFX come from one sprite decoded once into an AudioContext buffer.
 */
import { LEARN_ASSET_BASE } from "../media/learnAssets";

const NARRATOR_VOICE_ID = "cgSgspJ2msm6clMCkdW9";
const CACHE_NAME = "pixley-learn-tts-v1";
const SFX_BASE = `${LEARN_ASSET_BASE}/sfx`;
const SFX_NAMES = [
  "pop", "correct", "wrong", "snap", "whoosh", "sparkle", "levelup", "pageturn", "pickup", "drumroll_short",
];

class LearnAudio {
  constructor() {
    this.apiClient = null;
    this.ctx = null;
    this.unlocked = false;
    this.muted = false;
    this.queue = [];
    this.playing = null; // { text, audio, resolve }
    this.gen = 0;
    this.mem = new Map(); // text -> object URL
    this.inflight = new Map(); // text -> Promise<string|null>
    this.sfxBuffers = new Map();
    this.sfxLoading = null;
    this.listeners = new Set();
    this._gestureBound = false;
  }

  configure({ apiClient } = {}) {
    if (apiClient) this.apiClient = apiClient;
    this._bindGesture();
  }

  onChange(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  _emit() {
    this.listeners.forEach((fn) => {
      try { fn(this.state()); } catch { /* ignore */ }
    });
  }

  state() {
    return { unlocked: this.unlocked, speaking: !!this.playing, muted: this.muted };
  }

  // ── Autoplay unlock ──────────────────────────────────────────────────
  _bindGesture() {
    if (this._gestureBound || typeof window === "undefined") return;
    this._gestureBound = true;
    const handler = () => this.unlock();
    ["pointerdown", "keydown", "touchend"].forEach((ev) =>
      window.addEventListener(ev, handler, { passive: true }),
    );
  }

  async unlock() {
    if (this.unlocked) return true;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        this.ctx = this.ctx || new AC();
        if (this.ctx.state === "suspended") await this.ctx.resume();
        const buf = this.ctx.createBuffer(1, 1, 22050);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        src.connect(this.ctx.destination);
        src.start(0);
      }
      this.unlocked = true;
      this._emit();
      this._pump();
      this._loadSfx();
      return true;
    } catch {
      return false;
    }
  }

  // ── TTS fetching + caching ───────────────────────────────────────────
  _cacheKey(text) {
    return `${LEARN_ASSET_BASE}/__tts/${NARRATOR_VOICE_ID}/${encodeURIComponent(text)}`;
  }

  async _fetchLine(text) {
    if (this.mem.has(text)) return this.mem.get(text);
    if (this.inflight.has(text)) return this.inflight.get(text);
    const p = (async () => {
      try {
        if (typeof caches !== "undefined") {
          const cache = await caches.open(CACHE_NAME);
          const hit = await cache.match(this._cacheKey(text));
          if (hit) {
            const url = URL.createObjectURL(await hit.blob());
            this.mem.set(text, url);
            return url;
          }
        }
        if (!this.apiClient?.quickTTS) return null;
        const base64 = await this.apiClient.quickTTS(text, NARRATOR_VOICE_ID);
        if (!base64) return null;
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: "audio/mpeg" });
        if (typeof caches !== "undefined") {
          try {
            const cache = await caches.open(CACHE_NAME);
            await cache.put(this._cacheKey(text), new Response(blob, { headers: { "Content-Type": "audio/mpeg" } }));
          } catch { /* quota — memory cache still works */ }
        }
        const url = URL.createObjectURL(blob);
        this.mem.set(text, url);
        return url;
      } catch {
        return null;
      } finally {
        this.inflight.delete(text);
      }
    })();
    this.inflight.set(text, p);
    return p;
  }

  /** Warm every line a course will say. Fire-and-forget, bounded concurrency. */
  prefetch(lines = []) {
    const uniq = [...new Set(lines.filter((l) => typeof l === "string" && l.trim()).map((l) => l.trim()))];
    let i = 0;
    const worker = async () => {
      while (i < uniq.length) {
        const t = uniq[i++];
        // eslint-disable-next-line no-await-in-loop
        await this._fetchLine(t);
      }
    };
    return Promise.all([worker(), worker(), worker()]);
  }

  // ── Queue ────────────────────────────────────────────────────────────
  /**
   * speak(text, { interrupt, priority }) → Promise resolved when the line
   * finished (or was skipped). interrupt clears the queue and stops current.
   */
  speak(text, { interrupt = false, dedupe = true } = {}) {
    const t = typeof text === "string" ? text.trim() : "";
    if (!t || this.muted) return Promise.resolve(false);
    if (interrupt) this.cancel();
    if (dedupe && (this.playing?.text === t || this.queue.some((q) => q.text === t))) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      this.queue.push({ text: t, resolve, gen: this.gen });
      this._pump();
    });
  }

  cancel() {
    this.gen += 1;
    this.queue.splice(0).forEach((q) => q.resolve(false));
    if (this.playing) {
      try { this.playing.audio.pause(); } catch { /* ignore */ }
      this.playing.resolve(false);
      this.playing = null;
      this._emit();
    }
  }

  async _pump() {
    if (this.playing || !this.queue.length) return;
    if (!this.unlocked) return; // resumes on unlock()
    const item = this.queue.shift();
    if (item.gen !== this.gen) { item.resolve(false); return this._pump(); }
    const url = await this._fetchLine(item.text);
    if (item.gen !== this.gen) { item.resolve(false); return this._pump(); }
    if (!url) { item.resolve(false); return this._pump(); }
    const audio = new Audio(url);
    audio.preload = "auto";
    this.playing = { text: item.text, audio, resolve: item.resolve };
    this._emit();
    const done = () => {
      if (this.playing?.audio === audio) {
        this.playing = null;
        item.resolve(true);
        this._emit();
        this._pump();
      }
    };
    audio.onended = done;
    audio.onerror = done;
    try {
      await audio.play();
    } catch {
      // NotAllowed → we weren't really unlocked; keep the line for later.
      this.unlocked = false;
      this.playing = null;
      this.queue.unshift(item);
      this._emit();
    }
    return undefined;
  }

  // ── SFX ──────────────────────────────────────────────────────────────
  async _loadSfx() {
    if (this.sfxLoading || !this.ctx) return this.sfxLoading;
    this.sfxLoading = (async () => {
      await Promise.all(
        SFX_NAMES.map(async (name) => {
          try {
            const res = await fetch(`${SFX_BASE}/${name}.mp3`, { cache: "force-cache" });
            if (!res.ok) return;
            const buf = await this.ctx.decodeAudioData(await res.arrayBuffer());
            this.sfxBuffers.set(name, buf);
          } catch { /* missing sfx is non-fatal */ }
        }),
      );
    })();
    return this.sfxLoading;
  }

  sfx(name, { volume = 0.9, rate = 1 } = {}) {
    if (this.muted || !this.ctx || !this.unlocked) return;
    const buf = this.sfxBuffers.get(name);
    if (!buf) return;
    try {
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = rate;
      const gain = this.ctx.createGain();
      gain.gain.value = volume;
      src.connect(gain).connect(this.ctx.destination);
      src.start(0);
    } catch { /* ignore */ }
  }

  setMuted(m) {
    this.muted = !!m;
    if (m) this.cancel();
    this._emit();
  }
}

export const learnAudio = new LearnAudio();
export default learnAudio;
